import { normalizaAlias } from '../normalizacion/normalizaAlias.js'
import { resolverValorEstricto } from '../motor/parser.js'

function stripWrapper(str) {
  let s = String(str || '').trim()
  if (s.startsWith('${') && s.endsWith('}')) s = s.slice(2, -1).trim()
  return s
}

function splitArgs(str) {
  const args = []
  let current = ''
  let inQuotes = false
  let escape = false
  let parenDepth = 0

  for (let i = 0; i < str.length; i++) {
    const c = str[i]
    if (escape) {
      current += c
      escape = false
      continue
    }
    if (c === '\\' && inQuotes) {
      current += c
      escape = true
      continue
    }
    if (c === '"') {
      current += c
      inQuotes = !inQuotes
      continue
    }
    if (!inQuotes && c === '(') {
      parenDepth++
      current += c
      continue
    }
    if (!inQuotes && c === ')') {
      parenDepth = Math.max(0, parenDepth - 1)
      current += c
      continue
    }
    if (!inQuotes && parenDepth === 0 && c === ',') {
      args.push(current.trim())
      current = ''
      continue
    }
    current += c
  }

  if (current.trim()) args.push(current.trim())
  return args
}

function parseCeldaArgs(raw) {
  const args = splitArgs(raw)
  return {
    tabla: (args[0] || '').trim(),
    campo: (args[1] || '').trim(),
    expr: args[2] != null ? args.slice(2).join(',').trim() : '',
  }
}

function parseTablaArgs(raw) {
  const args = splitArgs(raw)
  return {
    nombre: (args[0] || '').trim(),
    nodo: (args[1] || '').trim(),
  }
}

function extraerVarDirecta(expresion) {
  const raw = stripWrapper(String(expresion || ''))
  const match = raw.match(/^(v:|i:)(.+)$/)
  if (!match) return null
  return {
    prefijo: match[1].slice(0, -1),
    ruta: match[2].trim(),
  }
}

function normalizePathArg(arg, aliasToVariable, aliasToExpresion) {
  if (!arg) return ''
  let limpio = arg.trim()
  limpio = limpio.replace(/\[\*\]$/, '').replace(/\[\]$/, '')

  if (aliasToVariable[limpio]?.fullPath) return aliasToVariable[limpio].fullPath
  if (aliasToExpresion[limpio]?.expresion) {
    const base = extraerVarDirecta(aliasToExpresion[limpio].expresion)
    if (base?.ruta) return base.ruta
  }

  const directa = extraerVarDirecta(limpio)
  if (directa?.ruta) return directa.ruta
  return limpio
}

function relativizarCampo(fullPath, basePath) {
  if (!fullPath) return ''
  if (!basePath) return fullPath
  if (fullPath === basePath) return ''

  if (fullPath.startsWith(basePath + '.')) {
    return fullPath.slice(basePath.length + 1)
  }

  if (fullPath.startsWith(basePath + '[')) {
    let rest = fullPath.slice(basePath.length)
    rest = rest.replace(/^\[\d+\]\.?/, '')
    if (rest.startsWith('.')) rest = rest.slice(1)
    return rest
  }

  return fullPath
}

function resolveCampoArg(campo, basePath, aliasToVariable, aliasToExpresion) {
  if (!campo) return ''
  const raw = campo.trim()
  if (/^(v:|i:)/.test(raw)) return raw

  let fullPath = aliasToVariable[raw]?.fullPath
  if (!fullPath && aliasToExpresion[raw]?.expresion) {
    const base = extraerVarDirecta(aliasToExpresion[raw].expresion)
    if (base?.ruta) fullPath = base.ruta
  }

  if (fullPath) return relativizarCampo(fullPath, basePath)
  return relativizarCampo(raw, basePath)
}

function pathWithIndex(basePath, idx) {
  if (!basePath) return ''
  if (basePath.includes('[*]')) return basePath.replace('[*]', `[${idx}]`)
  if (basePath.includes('[]')) return basePath.replace('[]', `[${idx}]`)
  return `${basePath}[${idx}]`
}

function joinPath(base, campo) {
  if (!campo) return base
  if (campo.startsWith('[')) return `${base}${campo}`
  return `${base}.${campo}`
}

function replaceTokenOutsideQuotes(str, token, replacement) {
  if (!str || !token) return str

  let out = ''
  let inQuotes = false
  let escape = false

  for (let i = 0; i < str.length; i++) {
    const c = str[i]
    if (escape) {
      out += c
      escape = false
      continue
    }
    if (c === '\\' && inQuotes) {
      out += c
      escape = true
      continue
    }
    if (c === '"') {
      out += c
      inQuotes = !inQuotes
      continue
    }
    if (!inQuotes && str.startsWith(token, i)) {
      out += replacement
      i += token.length - 1
      continue
    }
    out += c
  }

  return out
}

function buscarTabla(nombreTabla, expresiones = []) {
  for (const expresion of expresiones) {
    const raw = stripWrapper(expresion?.expresion || '')
    if (!raw.startsWith('tabla:')) continue

    const tabla = parseTablaArgs(raw.slice('tabla:'.length))
    if (tabla.nombre === nombreTabla) return tabla
  }

  return null
}

function contarDecimales(valor) {
  const limpio = String(valor).trim()
  const partes = limpio.split('.')
  return partes.length > 1 ? partes[1].length : 0
}

function parsearNumero(valor, fila) {
  const texto = String(valor).trim()
  if (!texto) return { vacio: true }
  if (!/^-?(?:\d+|\d*\.\d+)$/.test(texto)) {
    return {
      error: `Error: El valor "${texto}" en la fila ${fila} no es numérico`,
    }
  }

  return {
    numero: Number(texto),
    decimales: contarDecimales(texto),
  }
}

function obtenerContexto(contexto) {
  if (!contexto || typeof contexto !== 'object') {
    throw new Error('sumarColumna requiere contexto de ejecución')
  }

  const datos = contexto.datos
  const variables = Array.isArray(contexto.variables) ? contexto.variables : null
  const expresiones = Array.isArray(contexto.expresiones) ? contexto.expresiones : null

  if (!datos || !variables || !expresiones || typeof contexto.ejecutarExpresion !== 'function') {
    throw new Error('sumarColumna requiere datos, variables, expresiones y evaluador en el contexto')
  }

  return { datos, variables, expresiones, ejecutarExpresion: contexto.ejecutarExpresion }
}

export function sumarColumna(nombreExpresionCelda, textoSinValores = '') {
  if (!String(nombreExpresionCelda || '').trim()) {
    throw new Error('El primer parámetro de sumarColumna debe ser una expresión celda')
  }

  const { datos, variables, expresiones, ejecutarExpresion } = obtenerContexto(this)
  const { aliasToVariable, aliasToExpresion } = normalizaAlias(variables, expresiones)
  const alias = String(nombreExpresionCelda).trim()
  const definicionCelda = aliasToExpresion[alias]

  if (!definicionCelda?.expresion) {
    throw new Error(`No existe la expresión "${alias}"`)
  }

  const rawCelda = stripWrapper(definicionCelda.expresion)
  if (!rawCelda.startsWith('celda:')) {
    throw new Error(`La expresión "${alias}" no es de tipo celda`)
  }

  const { tabla, campo, expr } = parseCeldaArgs(rawCelda.slice('celda:'.length))
  if (!tabla) {
    throw new Error(`La expresión "${alias}" no define una tabla`)
  }

  const definicionTabla = buscarTabla(tabla, expresiones)
  if (!definicionTabla) {
    throw new Error(`No existe la tabla "${tabla}" asociada a la expresión "${alias}"`)
  }

  const basePath = normalizePathArg(definicionTabla.nodo, aliasToVariable, aliasToExpresion)
  if (!basePath) {
    throw new Error(`El nodo principal de la tabla "${tabla}" es inválido`)
  }

  const filas = resolverValorEstricto(datos, basePath)
  if (!Array.isArray(filas)) {
    throw new Error(`El nodo principal de la tabla "${tabla}" no resuelve a un array: ${basePath}`)
  }

  if (filas.length === 0) {
    return String(textoSinValores ?? '')
  }

  const campoResolvido = resolveCampoArg(campo, basePath, aliasToVariable, aliasToExpresion)
  if (!campoResolvido && !expr) {
    throw new Error(`No se pudo resolver la columna de la expresión "${alias}"`)
  }

  let suma = 0
  let totalValores = 0
  let maxDecimales = 0

  for (let i = 0; i < filas.length; i++) {
    const filaActual = filas[i]
    const basePathIdx = pathWithIndex(basePath, i)
    const baseExpr = /^(v:|i:)/.test(campoResolvido)
      ? campoResolvido
      : `v:${joinPath(basePathIdx, campoResolvido)}`

    const errores = []
    const valor = expr
      ? ejecutarExpresion(
        replaceTokenOutsideQuotes(stripWrapper(expr), '@v', baseExpr),
        datos,
        errores
      )
      : /^(v:|i:)/.test(campoResolvido)
        ? ejecutarExpresion(baseExpr, datos, errores)
        : resolverValorEstricto(filaActual, campoResolvido)

    if (errores.length) {
      const detalle = errores[0]?.detail ? `: ${errores[0].detail}` : ''
      throw new Error(`${errores[0]?.message || 'Error evaluando la expresión'}${detalle}`)
    }

    const numero = parsearNumero(valor, i + 1)
    if (numero.error) throw new Error(numero.error)
    if (numero.vacio) continue

    suma += numero.numero
    totalValores++
    maxDecimales = Math.max(maxDecimales, numero.decimales || 0)
  }

  if (totalValores === 0) return String(textoSinValores ?? '')
  return maxDecimales > 0 ? suma.toFixed(maxDecimales) : String(suma)
}
